import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { type ClientsParams, clientsExportUrl, type ClientsReport } from '@/features/reports/api';
import { exportFileName } from '@/features/reports/clients/labels';
import { ClientName } from '@/features/reports/clients/SpendView';
import { CLIENTS_COPY } from '@/features/reports/copy';
import { formatPercent, formatPounds } from '@/features/reports/format';
import { ReportCard, Tile } from '@/features/reports/parts';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { formatDate } from '@/lib/format';

const COPY = CLIENTS_COPY.dropOff;

/**
 * Clients › Drop-off (ALS-87): who bought in the same days a year before and has not bought in
 * this period, like for like to date, as the period's comparison is. The list runs by what each
 * spent then, so the biggest loss is read first.
 */
export function DropOffView({ report, params }: { report: ClientsReport; params: ClientsParams }) {
    const drop = report.dropOff;
    const definition = COPY.definition(drop.thisYear.label, drop.lastYear.label);

    return (
        <>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <Tile
                    label={COPY.tiles.lastYear}
                    value={String(drop.lastYearClients)}
                    note={COPY.lastYearNote(drop.lastYear.label)}
                    definition={definition}
                />
                <Tile
                    label={COPY.tiles.retained}
                    value={String(drop.retained)}
                    note={drop.thisYear.label}
                />
                <Tile
                    label={COPY.tiles.droppedOff}
                    value={String(drop.droppedOff)}
                    note={drop.thisYear.label}
                />
                <Tile
                    label={COPY.tiles.rate}
                    value={drop.dropOffRate === null ? '–' : formatPercent(drop.dropOffRate)}
                    note={CLIENTS_COPY.clients(drop.droppedOff)}
                />
            </div>

            <ReportCard
                title={COPY.card}
                subtitle={COPY.subtitle(drop.lastYear.label)}
                actions={
                    <ExportButton
                        url={clientsExportUrl('DropOff', params)}
                        fileName={exportFileName(['drop-off', params.period])}
                        disabled={drop.clients.length === 0}
                    />
                }
            >
                {drop.clients.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.empty}</p>
                ) : (
                    <Table stack region={COPY.card} className="table-stack--wide-labels">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{COPY.columns.client}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.columns.lastYear}
                                </TableHead>
                                <TableHead>{COPY.columns.lastOrder}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.columns.lifetime}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {drop.clients.map((c) => (
                                <TableRow key={c.customerId}>
                                    <TableCell data-stack="title">
                                        <ClientName
                                            customerId={c.customerId}
                                            name={c.client}
                                            manager={c.relationshipManager}
                                            known={c.isKnown}
                                        />
                                    </TableCell>
                                    <TableNum data-stack="status" className="font-medium">
                                        {formatPounds(c.lastYearSpend)}
                                    </TableNum>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.columns.lastOrder}
                                        className="text-muted-foreground whitespace-nowrap"
                                    >
                                        {formatDate(c.lastOrderOn)}
                                    </TableCell>
                                    <TableNum data-stack="meta" data-label={COPY.columns.lifetime}>
                                        {formatPounds(c.lifetime)}
                                    </TableNum>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ReportCard>
        </>
    );
}
